import * as React from "react"
import {
Dialog,
DialogContent,
DialogDescription,
DialogFooter,
DialogHeader,
DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"

interface DeleteConfirmationDialogProps {
  /** Controls whether the dialog is open */
  open: boolean
  /** Callback for when the dialog's open state changes */
  onOpenChange: (open: boolean) => void
  /** The title of the dialog */
  title: React.ReactNode
  /** The description of the dialog, supports React nodes for formatted text */
  description: React.ReactNode
  /** Callback for when the confirm button is clicked */
  onConfirm: () => void | Promise<void>
  /** The label for the confirm button, defaults to "Delete" */
  confirmLabel?: string
  /** The label for the cancel button, defaults to "Cancel" */
  cancelLabel?: string
  /** Whether a deletion is in progress, disables buttons and shows a loading state */
  isPending?: boolean
  /** Custom class name for the confirm button */
  confirmClassName?: string
  /** Custom class name for the dialog content */
  contentClassName?: string
}

/**
 * A reusable, "pro-tool" design compliant deletion confirmation dialog.
 * Follows Standardized UI/UX, featuring a ghost cancel button and a high-visibility red action button.
 */
export function DeleteConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  isPending = false,
  confirmClassName,
  contentClassName,
}: DeleteConfirmationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className={cn("sm:max-w-[425px]", contentClassName)}
        showCloseButton={false}
        onPointerDownOutside={(e) => {
          if (isPending) e.preventDefault()
        }}
        onEscapeKeyDown={(e) => {
          if (isPending) e.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button
            type="button"
            variant="ghost"
            disabled={isPending}
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground border-none shadow-none hover:bg-transparent focus:ring-0"
          >
            {cancelLabel}
          </Button>
          <Button
            disabled={isPending}
            onClick={(e) => {
              e.preventDefault()
              void onConfirm()
            }}
            className={cn(
              "bg-red-600 hover:bg-red-700 text-white min-w-[120px] focus:ring-red-600 transition-all duration-200",
              confirmClassName
            )}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              confirmLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

